import { Tabs, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useCallback, useMemo, useState } from "react";
import { Chart } from "react-google-charts";

import { Col, Row } from "@/components/customComponents";
import { useTheme } from "@/hooks";
import {
  buildChartData,
  buildChartOptions,
  getChartColors,
  getChartPlaceholder,
  getSeriesLabels,
  stripHiddenColumns,
} from "@/pages/CreatorAnalytics/chartUtils";
import { METRIC_TABS } from "@/pages/CreatorAnalytics/constants";
import type { ITimeseriesPoint, MetricTab } from "@/pages/CreatorAnalytics/types";
import { useContainerWidth } from "@/pages/CreatorAnalytics/useContainerWidth";

import LegendButton from "./LegendButton";

interface ITrendChartsProps {
  timeseries: ITimeseriesPoint[];
  favoritesAvailableFrom: string;
}

const TrendCharts = ({ timeseries, favoritesAvailableFrom }: ITrendChartsProps) => {
  const [stateActiveTab, setStateActiveTab] = useState<MetricTab>("requests");
  const [stateHidden, setStateHidden] = useState<Set<number>>(new Set());
  const { isDark } = useTheme();
  const { refContainer, width: containerWidth } = useContainerWidth();

  const { text: textColor, gridline: gridlineColor } = useMemo(() => getChartColors(isDark), [isDark]);

  const fullChartData = useMemo(
    () => buildChartData(stateActiveTab, timeseries, favoritesAvailableFrom),
    [stateActiveTab, timeseries, favoritesAvailableFrom],
  );

  const seriesLabels = useMemo(() => getSeriesLabels(fullChartData), [fullChartData]);

  const visibleChartData = useMemo(() => stripHiddenColumns(fullChartData, stateHidden), [fullChartData, stateHidden]);

  const googleChartData = useMemo(() => [visibleChartData.columns, ...visibleChartData.rows], [visibleChartData]);

  const chartOptions = useMemo(
    () => ({
      ...buildChartOptions(visibleChartData, stateActiveTab, textColor, gridlineColor),
      legend: { position: "none" as const },
    }),
    [visibleChartData, stateActiveTab, textColor, gridlineColor],
  );

  const tabItems = useMemo(() => METRIC_TABS.map(({ key, label }) => ({ name: key, label })), []);

  const handleTabChange = useCallback((tabName: string) => {
    setStateActiveTab(tabName as MetricTab);
    setStateHidden(new Set());
  }, []);

  const handleToggleSeries = useCallback((idx: number) => {
    setStateHidden((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  }, []);

  const isAllHidden = stateHidden.size >= seriesLabels.length;

  return (
    <Col className="gap-4">
      <Row className="flex-wrap items-center gap-2">
        <Text variant={TextVariants.HEADING_6_BOLD}>Daily Trends</Text>
        <Row className="ml-auto overflow-x-auto">
          <Tabs tabItems={tabItems} activeTab={stateActiveTab} onTabChange={handleTabChange} />
        </Row>
      </Row>

      {stateActiveTab === "favorites" && timeseries[0]?.date < favoritesAvailableFrom && (
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-warning">
          Favorites data is only available from {favoritesAvailableFrom}. Earlier dates are not shown.
        </Text>
      )}

      <div
        ref={refContainer}
        className="min-h-[280px] w-full rounded-xl border border-default-200 bg-content1 p-2 dark:border-default-100 sm:min-h-[350px] sm:p-4"
      >
        <Row className="mb-2 flex-wrap justify-center gap-4">
          {seriesLabels.map((label, idx) => (
            <LegendButton
              key={label}
              label={label}
              index={idx}
              color={fullChartData.colors[idx]}
              textColor={textColor}
              isHidden={stateHidden.has(idx)}
              onToggle={handleToggleSeries}
            />
          ))}
        </Row>

        {!isAllHidden && googleChartData.length > 1 && containerWidth > 0 ? (
          <Chart chartType="LineChart" width={`${containerWidth}px`} height="300px" data={googleChartData} options={chartOptions} />
        ) : (
          <Col className="flex h-[300px] items-center justify-center">
            <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-default-400">
              {getChartPlaceholder(isAllHidden, containerWidth)}
            </Text>
          </Col>
        )}
      </div>
    </Col>
  );
};

export default TrendCharts;
